import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { api } from '../../../context/AuthContext';
import { toast } from 'sonner';

const FIELDS = [
  ['actual_return_pct', 'Actual return (%)', 'number'],
  ['actual_hold_months', 'Actual hold (months)', 'number'],
  ['capital_returned', 'Capital returned (€)', 'number'],
  ['exit_value', 'Exit value (€)', 'number'],
  ['renovation_cost', 'Renovation cost (€)', 'number'],
  ['total_project_cost', 'Total project cost (€)', 'number'],
  ['investor_distribution', 'Investor distribution (€)', 'number'],
  ['exit_date', 'Exit date', 'date'],
];

const EMPTY = FIELDS.reduce((acc, [key]) => ({ ...acc, [key]: '' }), { methodology_note: '' });

/**
 * Exit results: realized performance that becomes the public Flippex track record.
 */
export const ProjectExitResultsPanel = ({ projectId, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [snapshot, setSnapshot] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchResults = useCallback(async () => {
    try {
      const res = await api.get(`/admin/v2/projects/${projectId}/exit-results`);
      const results = res.data.exit_results;
      setSnapshot(res.data.projection_snapshot);
      if (results) {
        setForm({
          ...EMPTY,
          ...Object.fromEntries(
            Object.entries(results).map(([k, v]) => [k, v === null || v === undefined ? '' : String(v).slice(0, k === 'exit_date' ? 10 : undefined)])
          ),
        });
      }
    } catch {
      setSnapshot(null);
    }
  }, [projectId]);

  useEffect(() => { fetchResults(); }, [fetchResults]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { mark_exited: true, methodology_note: form.methodology_note || null };
      FIELDS.forEach(([key, , type]) => {
        const raw = form[key];
        if (raw === '' || raw === null) {
          payload[key] = null;
        } else {
          payload[key] = type === 'number' ? Number(raw) : raw;
        }
      });
      const res = await api.put(`/admin/v2/projects/${projectId}/exit-results`, payload);
      setSnapshot(res.data.projection_snapshot);
      toast.success('Exit results saved — project is now public track record');
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save exit results');
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    try {
      await api.delete(`/admin/v2/projects/${projectId}/exit-results`);
      setForm(EMPTY);
      toast.success('Exit results cleared');
      onSaved?.();
    } catch {
      toast.error('Could not clear exit results');
    }
  };

  return (
    <section className="bg-white rounded-xl border border-black/[0.06]" data-testid="project-exit-results-panel">
      <div className="px-5 py-4 border-b border-black/[0.06]">
        <h3 className="text-sm font-semibold">Exit results</h3>
        <p className="text-xs text-slate-500 mt-1">
          Recording these figures marks the project as exited and publishes it as realized track record. Only enter
          verified numbers — empty fields stay hidden from investors.
        </p>
      </div>

      <div className="p-5 space-y-4">
        {snapshot && (
          <div className="p-3 rounded-lg bg-slate-50 text-xs text-slate-600" data-testid="projection-snapshot">
            Frozen projection: {snapshot.projected_return_pct ?? '—'}% return ·{' '}
            {snapshot.target_hold_months ?? '—'} months target hold
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FIELDS.map(([key, label, type]) => (
            <div key={key}>
              <Label className="text-xs">{label}</Label>
              <Input
                type={type}
                value={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                className="mt-1"
                data-testid={`exit-field-${key}`}
              />
            </div>
          ))}
        </div>

        <div>
          <Label className="text-xs">Return methodology note (optional)</Label>
          <Textarea
            value={form.methodology_note}
            onChange={(e) => setForm({ ...form, methodology_note: e.target.value })}
            rows={2}
            placeholder="e.g. Actual return is the total return on invested capital over the full hold period."
            className="mt-1"
            data-testid="exit-field-methodology"
          />
        </div>

        <div className="flex items-center gap-3">
          <Button onClick={save} disabled={saving} className="bg-pink-600 hover:bg-pink-700" data-testid="save-exit-results">
            {saving ? 'Saving...' : 'Save exit results'}
          </Button>
          <Button variant="outline" onClick={clear} data-testid="clear-exit-results">
            Clear
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ProjectExitResultsPanel;
